import {
  hasAdminAccess,
  hasClientSystemAdminAccess,
} from 'common/src/authorization/accessChecks';
import {
  getRolesAllowedToEdit,
  isRoleAllowedInGroup,
} from 'common/src/authorization/editAccess';
import { healPhoneNumberValue } from 'common/src/transformers/healPhoneNumberValue';
import { Role } from 'common/src/types/enums/Role.enum';
import {
  IAuthenticatedUser,
  IOrganization,
  IOrganizationGroup,
  IUser,
  IUserAlertPreferences,
} from 'common/src/types/interfaces';

import {
  getEmailField,
  getPhoneNumberField,
} from 'portal/src/components/common/forms/commonFormFields';
import {
  autoCompleteType,
  noFieldType,
  patternInputType,
  selectInputType,
  textInputType,
} from 'portal/src/components/common/forms/FormFieldTypes';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';

type IUserAlertPreferenceFormValue = Omit<
  IUserAlertPreferences,
  'id' | 'userId'
>;

export type UserFormOrganization = {
  id: string;
  name?: string;
};

export interface IUserFormValues {
  alertPreferences: Array<Partial<IUserAlertPreferenceFormValue>>;
  email: string;
  name: string;
  organizationId?: string;
  organizationGroups?: string[];
  phone: string;
  profilePicture: string;
  title: string;
  userType: string;
}

export const defaultValues = (
  entity: Partial<IUser> = {},
  defaultOrgId?: string,
): IUserFormValues => ({
  alertPreferences:
    entity.alertPreferences?.map(
      ({ id: _id, userId: _userId, ...preference }) => ({
        ...preference,
      }),
    ) ?? [],
  name: entity.name ?? '',
  email: entity.email ?? '',
  userType: entity.userType ?? '',
  title: entity.title ?? '',
  organizationId: entity.organizationId ?? defaultOrgId,
  phone: healPhoneNumberValue(entity.phone ?? ''),
  profilePicture: entity.profilePicture ?? '',
  organizationGroups: entity.organizationGroups?.map((group) => group.id) ?? [],
});

const mapOrgToSelectValue = (org: UserFormOrganization) => ({
  label: org?.name,
  value: org?.id,
});

export const getFields = (
  loggedInUser: IAuthenticatedUser,
  entity: {
    id: string;
    organizationId: string;
    orgGroups: Partial<IOrganizationGroup>[];
  },
  organizations: IOrganization[],
  organizationGroups: IOrganizationGroup[] = [],
  selectedUserRole: Role,
  isNewOrg: boolean,
  orgIdDisabled: boolean,
) => {
  const isNewUser = entity.id === NEW_ID;
  const isOwnProfile = entity.id === loggedInUser.id;

  const lgdInUserIsSysAdmin = hasClientSystemAdminAccess(loggedInUser.userType);
  const selectedRoleIsGroupRole = isRoleAllowedInGroup(selectedUserRole);

  const allowedUserRoles = getRolesAllowedToEdit(loggedInUser.userType);
  const selectedRoleIsAboveLgdInUser =
    !allowedUserRoles.includes(selectedUserRole);

  const orgIsDisabled = isNewOrg || orgIdDisabled || !lgdInUserIsSysAdmin;
  const userRoleIsDisabled =
    !isNewUser &&
    (isOwnProfile ||
      !hasAdminAccess(loggedInUser.userType) ||
      selectedRoleIsAboveLgdInUser);
  const orgGroupsIsDisabled =
    isNewOrg ||
    (!lgdInUserIsSysAdmin && selectedRoleIsAboveLgdInUser) ||
    !selectedRoleIsGroupRole;

  const formFieldsConfiguration: (
    | autoCompleteType
    | textInputType
    | selectInputType
    | patternInputType
    | noFieldType
  )[] = [
    {
      name: 'name',
      label: 'Name',
      placeholder: 'Name',
      required: true,
      type: 'textInput',
    },
    getEmailField(true, !isNewUser),
    {
      name: 'organizationId',
      label: 'Organization',
      placeholder: 'Organization',
      type: 'selectInput',
      required: !isNewOrg && lgdInUserIsSysAdmin,
      disabled: orgIsDisabled,
      values: orgIsDisabled
        ? // Try to find the entity's oganization in the list to ensure we have complete data. Otherwise, add the organization as is.
          [mapOrgToSelectValue({ id: entity.organizationId })]
        : organizations.map(mapOrgToSelectValue),
      hidden: orgIsDisabled,
    },
    {
      name: 'userType',
      label: 'User Role',
      placeholder: 'User Role',
      type: 'selectInput',
      required: true,
      disabled: userRoleIsDisabled,
      values: userRoleIsDisabled
        ? [{ label: selectedUserRole, value: selectedUserRole }]
        : allowedUserRoles.map((item) => ({ label: item, value: item })),
    },
    {
      label: 'Organization Groups',
      name: 'organizationGroups',
      placeholder: 'Organization Groups',
      type: 'autocomplete',
      required: selectedRoleIsGroupRole,
      disabled: orgGroupsIsDisabled,
      multiple: true,
      options: (orgGroupsIsDisabled
        ? entity.orgGroups
        : organizationGroups
      ).map((item) => ({ label: item.name, value: item.id })),
      hidden: orgGroupsIsDisabled,
    },
    {
      name: 'noField',
      type: 'noField',
      hidden: orgIsDisabled || orgGroupsIsDisabled,
    },
    {
      name: 'title',
      label: 'Title',
      placeholder: 'Title',
      type: 'textInput',
      required: false,
    },
    getPhoneNumberField(false),
  ];
  return formFieldsConfiguration;
};
